import { webPaths } from "@zplanem/web-paths"
import { useMemo } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useAuth } from "../auth-context"
import { PageContainer } from "../components/page-container"
import { PageHeader } from "../components/page-header"
import { PropertyCard } from "../components/property-card"
import { PropertyHeroCard } from "../components/property-hero-card"
import { useDashboardPropertyChannels } from "../hooks/use-dashboard-property-channels"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { useProperties } from "../hooks/use-properties"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

function PlusIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

export function DashboardPage() {
	const { user } = useAuth()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { data: properties, isLoading, error } = useProperties()
	const { lastPropertyId } = useLastPropertyId()

	const { hero, others } = useMemo(() => {
		if (!properties || properties.length === 0) {
			return { hero: null, others: [] as typeof properties }
		}
		const preferred = lastPropertyId
			? properties.find((p) => p.id === lastPropertyId)
			: undefined
		// properties already sorted by createdAt desc
		const heroPick = preferred ?? properties[0]
		return {
			hero: heroPick,
			others: properties.filter((p) => p.id !== heroPick.id),
		}
	}, [properties, lastPropertyId])

	// Live-refresh the list when any visible property's name/description changes.
	const propertyIds = useMemo(
		() => (properties ?? []).map((p) => p.id),
		[properties],
	)
	useDashboardPropertyChannels(propertyIds)

	if (!user) return null

	return (
		<div className="min-h-screen w-full bg-background flex flex-col overflow-x-hidden">
			<PageContainer chrome="bare">
				{properties && properties.length > 0 ? (
					<PageHeader
						className="mb-8 md:mb-12"
						title={t.properties.propertiesListHeading}
						meta={
							<Link to={webPaths.newProperty()} className="shrink-0">
								{/* Separate icon/text buttons (not one with a hidden SVG):
								    a hidden-but-in-DOM <svg> still trips the Button's
								    has-[>svg]:px-4 rule and shrinks the desktop padding. */}
								<Button
									variant="brand"
									size="icon"
									className="md:hidden"
									aria-label={t.properties.newPropertyCta}
								>
									<PlusIcon />
								</Button>
								<Button
									variant="brand"
									size="lg"
									className="hidden md:inline-flex"
								>
									{t.properties.newPropertyCta}
								</Button>
							</Link>
						}
					/>
				) : (
					<header className="mb-8 md:mb-12">
						<h1 className="text-3xl md:text-5xl font-black text-foreground tracking-tight mb-2">
							{t.dashboard.title}
						</h1>
						<h2 className="text-xl md:text-2xl font-bold text-[color:var(--ring)]">
							{t.dashboard.welcome}
						</h2>
					</header>
				)}

				{isLoading ? (
					<div className="grid gap-6">
						<div className="h-56 bg-muted/40 rounded-lg border border-border animate-pulse" />
						<div className="grid md:grid-cols-3 gap-4">
							<div className="h-32 bg-muted/40 rounded-lg border border-border animate-pulse" />
							<div className="h-32 bg-muted/40 rounded-lg border border-border animate-pulse" />
							<div className="h-32 bg-muted/40 rounded-lg border border-border animate-pulse" />
						</div>
					</div>
				) : error ? (
					<div
						role="alert"
						className="rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-destructive"
					>
						{error.message}
					</div>
				) : properties && properties.length === 0 ? (
					<Link
						to={webPaths.newProperty()}
						className="block bg-muted/40 backdrop-blur-md p-12 rounded-lg border border-border hover:border-foreground/25 transition-colors text-center"
					>
						<h3 className="text-3xl font-black text-foreground">
							{t.properties.emptyStateTitle}
						</h3>
						<p className="mt-2 text-muted-foreground">
							{t.properties.emptyStateBody}
						</p>
						<div className="mt-6 inline-flex">
							<Button variant="brand" size="lg" asChild>
								<span>{t.properties.addFirstPropertyCta}</span>
							</Button>
						</div>
					</Link>
				) : hero ? (
					<div className="grid gap-6">
						<PropertyHeroCard property={hero} />
						{others.length > 0 ? (
							<section>
								<h3 className="text-xs uppercase tracking-[0.12em] text-muted-foreground/80 mb-3">
									{t.properties.propertiesListHeading}
								</h3>
								<div className="grid md:grid-cols-3 gap-4">
									{others.map((p) => (
										<PropertyCard key={p.id} property={p} />
									))}
								</div>
							</section>
						) : null}
					</div>
				) : null}
			</PageContainer>
		</div>
	)
}
